import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { ArrowDown, ArrowUp, ArrowUpDown, Pencil, Search } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLocale } from '../contexts/LocaleContext';
import { useProjectAccess } from '../contexts/ProjectAccessContext';
import { useToast } from '../contexts/ToastContext';
import { API_BASE } from '../config';
import { useShiftClickRowSelection } from '../hooks/useShiftClickRowSelection';
import { useIsMobile } from '../hooks/use-mobile';
import { shouldShowHeaderCreateActions } from '../utils/header-create-actions';
import { ProjectPanelLayout } from './ProjectPanelLayout';
import { PROJECT_SECTION_ICONS } from '../utils/concept-icons';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { Button } from './ui/button';
import { CancelButton } from './ui/cancel-button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
  DialogPrimaryShortcut,
} from './ui/dialog';
import {
  ENTITY_DETAIL_DIALOG_CONTENT_CLASSNAME,
  EntityDetailModalLayout,
  EntityDetailModalTitle,
} from './EntityDetailModalLayout';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { EntityTypeIcon, getEntityTypeLabelKey } from './EntityTypeIcon';
import { cn } from '@/lib/utils';
import { computeSimilarTagGroups, groupSignature } from '../utils/tag-similarity';
import { handleModifierAwareNavigate } from '../utils/app-navigation';
import { textIncludesQuery } from '@narrativebench/editor-core';
import { DataTableRowCheckbox } from './DataTableRowCheckbox';
import { DataTableSelectAllCheckbox } from './DataTableSelectAllCheckbox';
import { DataTableSelectionActions } from './DataTableSelectionActions';
import { ConfirmDialog } from './ConfirmDialog';
import { AddItemButton } from './AddItemButton';
import { ButtonTooltip, TooltipProvider } from './ui/tooltip';
import { DisabledTooltip } from './EmptyStateSecondaryButton';
import { RotatingDeleteIcon } from './RotatingDeleteIcon';

interface TagEntity {
  type: 'resource' | 'timeline_item';
  id: string;
  title: string;
}

interface TagOverviewRow {
  tag: string;
  usageCount: number;
  entities: TagEntity[];
}

function tagMatchesSubstringFilter(tag: string, query: string): boolean {
  return textIncludesQuery(tag, query);
}

function formatSimilarGroupChipLabel(group: string[]): string {
  if (group.length <= 4) {
    return group.join(', ');
  }
  return `${group.slice(0, 3).join(', ')} +${group.length - 3}`;
}

export const TagsManager: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tagParam = searchParams.get('tag');
  const { token } = useAuth();
  const { t } = useLocale();
  const { showErrorToast, showToast } = useToast();
  const { canEditProject } = useProjectAccess();
  const isMobile = useIsMobile();
  const [rows, setRows] = useState<TagOverviewRow[]>([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [sorting, setSorting] = useState<SortingState>([]);
  const getVisibleRowIdsRef = useRef<() => string[]>(() => []);
  const {
    rowSelection,
    setRowSelection,
    selectedIds: selectedTagNames,
    selectRangeTo,
    toggleRowSelected: toggleRowSelectedBase,
    clearSelection,
    setAnchorRowId,
    handleRowClick,
  } = useShiftClickRowSelection({
    getVisibleRowIds: () => getVisibleRowIdsRef.current(),
  });
  const [orderedSelectedTags, setOrderedSelectedTags] = useState<string[]>([]);
  const [detailRow, setDetailRow] = useState<TagOverviewRow | null>(null);
  const [detailName, setDetailName] = useState('');
  const [detailSaving, setDetailSaving] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeName, setMergeName] = useState('');
  const [mergeSaving, setMergeSaving] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createSaving, setCreateSaving] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [pendingDeleteTags, setPendingDeleteTags] = useState<string[]>([]);
  const [filterText, setFilterText] = useState('');
  const [showSimilarGroups, setShowSimilarGroups] = useState(false);
  const [selectedGroupSet, setSelectedGroupSet] = useState<Set<string> | null>(null);

  const loadOverview = useCallback(async (): Promise<void> => {
    if (!projectId || !token) {
      return;
    }
    setInitialLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/projects/${projectId}/tags/overview`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        throw new Error('overview failed');
      }
      const data = await response.json();
      setRows(Array.isArray(data.tags) ? data.tags : []);
    } catch {
      showErrorToast(t('tag_manager_failed_load'));
      setRows([]);
    } finally {
      setInitialLoading(false);
    }
  }, [projectId, token, t, showErrorToast]);

  useEffect(() => {
    void loadOverview();
  }, [loadOverview]);

  useEffect(() => {
    if (!tagParam || initialLoading) {
      return;
    }
    const row = rows.find((entry) => entry.tag === tagParam);
    if (row) {
      setDetailRow(row);
      setDetailName(row.tag);
    }
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('tag');
        return next;
      },
      { replace: true }
    );
  }, [tagParam, initialLoading, rows, setSearchParams]);

  const mergeDefaultName = useMemo(() => {
    for (const tag of orderedSelectedTags) {
      if (selectedTagNames.includes(tag)) {
        return tag;
      }
    }
    return selectedTagNames[0] ?? '';
  }, [orderedSelectedTags, selectedTagNames]);

  const similarGroups = useMemo(
    () => computeSimilarTagGroups(rows.map((r) => r.tag)),
    [rows]
  );

  const filteredRows = useMemo(() => {
    let list = rows;
    if (selectedGroupSet) {
      list = list.filter((row) => selectedGroupSet.has(row.tag));
    }
    return list.filter((row) => tagMatchesSubstringFilter(row.tag, filterText));
  }, [rows, filterText, selectedGroupSet]);

  const handleToggleSimilarGroups = useCallback((): void => {
    setShowSimilarGroups((prev) => {
      if (prev) {
        setSelectedGroupSet(null);
      }
      return !prev;
    });
  }, []);

  const handleSimilarGroupClick = useCallback((group: string[]): void => {
    const nextSig = groupSignature(group);
    setSelectedGroupSet((prev) => {
      if (prev !== null && groupSignature(Array.from(prev)) === nextSig) {
        return null;
      }
      return new Set(group);
    });
  }, []);

  useEffect(() => {
    setOrderedSelectedTags((prev) => {
      const kept = prev.filter((tag) => selectedTagNames.includes(tag));
      const added = selectedTagNames.filter((tag) => !prev.includes(tag));
      if (kept.length === prev.length && added.length === 0) {
        return prev;
      }
      return [...kept, ...added];
    });
  }, [selectedTagNames]);

  const toggleRowSelected = toggleRowSelectedBase;

  const clearTagSelection = useCallback((): void => {
    clearSelection();
    setOrderedSelectedTags([]);
  }, [clearSelection]);

  const openDetail = useCallback((row: TagOverviewRow): void => {
    setDetailRow(row);
    setDetailName(row.tag);
  }, []);

  const openCreate = useCallback((): void => {
    setCreateName('');
    setCreateOpen(true);
  }, []);

  const handleCreateSave = async (): Promise<void> => {
    if (!projectId || !token) {
      return;
    }
    const next = createName.trim();
    if (!next) {
      return;
    }
    setCreateSaving(true);
    try {
      const response = await fetch(`${API_BASE}/api/projects/${projectId}/tags`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name: next }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error((err as { error?: string }).error || 'create failed');
      }
      showToast(t('tag_manager_created'));
      setCreateOpen(false);
      setCreateName('');
      await loadOverview();
    } catch (e) {
      const message = e instanceof Error ? e.message : t('tag_manager_failed_create');
      showErrorToast(message);
    } finally {
      setCreateSaving(false);
    }
  };

  const handleDetailSave = async (): Promise<void> => {
    if (!projectId || !token || !detailRow) {
      return;
    }
    const next = detailName.trim();
    if (!next || next === detailRow.tag) {
      setDetailRow(null);
      return;
    }
    setDetailSaving(true);
    try {
      const response = await fetch(`${API_BASE}/api/projects/${projectId}/tags/merge`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ sourceTags: [detailRow.tag], targetName: next }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error((err as { error?: string }).error || 'merge failed');
      }
      showToast(t('tag_manager_updated'));
      setDetailRow(null);
      clearTagSelection();
      await loadOverview();
    } catch (e) {
      const message = e instanceof Error ? e.message : t('tag_manager_failed_merge');
      showErrorToast(message);
    } finally {
      setDetailSaving(false);
    }
  };

  const handleMergeSave = async (): Promise<void> => {
    if (!projectId || !token) {
      return;
    }
    const target = mergeName.trim();
    if (!target || selectedTagNames.length < 2) {
      return;
    }
    setMergeSaving(true);
    try {
      const response = await fetch(`${API_BASE}/api/projects/${projectId}/tags/merge`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ sourceTags: selectedTagNames, targetName: target }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error((err as { error?: string }).error || 'merge failed');
      }
      showToast(t('tag_manager_updated'));
      setMergeOpen(false);
      clearTagSelection();
      await loadOverview();
    } catch (e) {
      const message = e instanceof Error ? e.message : t('tag_manager_failed_merge');
      showErrorToast(message);
    } finally {
      setMergeSaving(false);
    }
  };

  const removeTags = useCallback(
    async (tags: string[]): Promise<void> => {
      if (!projectId || !token || tags.length === 0) {
        return;
      }
      setBulkDeleting(true);
      try {
        const response = await fetch(`${API_BASE}/api/projects/${projectId}/tags/remove`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ tags }),
        });
        if (!response.ok) {
          const err = await response.json().catch(() => ({}));
          throw new Error((err as { error?: string }).error || 'remove failed');
        }
        showToast(t('tag_manager_updated'));
        clearTagSelection();
        setDetailRow(null);
        await loadOverview();
      } catch (e) {
        const message = e instanceof Error ? e.message : t('tag_manager_failed_delete');
        showErrorToast(message);
      } finally {
        setBulkDeleting(false);
      }
    },
    [projectId, token, t, showToast, showErrorToast, loadOverview, clearTagSelection]
  );

  const requestBulkDelete = useCallback((): void => {
    if (selectedTagNames.length === 0) {
      return;
    }
    setPendingDeleteTags([...selectedTagNames]);
  }, [selectedTagNames]);

  const handleRowDelete = useCallback((tag: string): void => {
    setPendingDeleteTags([tag]);
  }, []);

  const confirmPendingDelete = useCallback((): void => {
    const tags = pendingDeleteTags;
    setPendingDeleteTags([]);
    if (tags.length === 0) {
      return;
    }
    void removeTags(tags);
  }, [pendingDeleteTags, removeTags]);

  const entityLink = (entity: TagEntity): string => {
    if (!projectId) {
      return '#';
    }
    if (entity.type === 'resource') {
      return `/projects/${projectId}/resources/${entity.id}`;
    }
    return `/projects/${projectId}/timeline?timelineItemId=${encodeURIComponent(entity.id)}`;
  };

  const columns = useMemo<ColumnDef<TagOverviewRow>[]>(
    () => {
      const cols: ColumnDef<TagOverviewRow>[] = [];
      if (canEditProject) {
        cols.push({
          id: 'select',
          header: ({ table }) => (
            <DataTableSelectAllCheckbox
              checked={
                table.getIsAllPageRowsSelected()
                  ? true
                  : table.getIsSomePageRowsSelected()
                    ? 'indeterminate'
                    : false
              }
              onCheckedChange={(checked) => {
                table.toggleAllPageRowsSelected(checked);
                const visibleIds = getVisibleRowIdsRef.current();
                setAnchorRowId(checked && visibleIds.length > 0 ? visibleIds[0] : null);
              }}
              label={t('tag_manager_select_all')}
            />
          ),
          cell: ({ row }) => (
            <DataTableRowCheckbox
              checked={row.getIsSelected()}
              rowId={row.id}
              ariaLabel={t('tag_manager_select_row')}
              onSelectRange={selectRangeTo}
              onToggle={toggleRowSelected}
            />
          ),
          enableSorting: false,
          size: 40,
        });
      }
      cols.push(
        {
          accessorKey: 'tag',
          header: ({ column }) => (
            <button
              type="button"
              onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
              className="flex items-center gap-2 px-2 py-1 rounded hover:bg-muted"
            >
              {t('Tags')}
              {column.getIsSorted() === 'asc' ? (
                <ArrowUp className="h-4 w-4" />
              ) : column.getIsSorted() === 'desc' ? (
                <ArrowDown className="h-4 w-4" />
              ) : (
                <ArrowUpDown className="h-4 w-4" />
              )}
            </button>
          ),
          cell: ({ row }) => <div className="font-medium">{row.original.tag}</div>,
        },
        {
          accessorKey: 'usageCount',
          header: ({ column }) => (
            <button
              type="button"
              onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
              className="flex items-center gap-2 px-2 py-1 rounded hover:bg-muted"
            >
              {t('tag_manager_usages')}
              {column.getIsSorted() === 'asc' ? (
                <ArrowUp className="h-4 w-4" />
              ) : column.getIsSorted() === 'desc' ? (
                <ArrowDown className="h-4 w-4" />
              ) : (
                <ArrowUpDown className="h-4 w-4" />
              )}
            </button>
          ),
          cell: ({ row }) => <div>{row.original.usageCount}</div>,
        },
      );
      if (canEditProject) {
        cols.push({
          id: 'actions',
          header: () => <div className="text-right">{t('tag_manager_actions')}</div>,
          cell: ({ row }) => (
            <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
                aria-label={t('edit')}
                onClick={() => openDetail(row.original)}
              >
                <Pencil className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="group h-8 w-8 p-0 text-destructive hover:text-destructive"
                aria-label={t('delete')}
                onClick={() => handleRowDelete(row.original.tag)}
              >
                <RotatingDeleteIcon />
              </Button>
            </div>
          ),
          enableSorting: false,
        });
      }
      return cols;
    },
    [
      t,
      toggleRowSelected,
      selectRangeTo,
      setAnchorRowId,
      openDetail,
      handleRowDelete,
      canEditProject,
    ]
  );

  const table = useReactTable({
    data: filteredRows,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    onRowSelectionChange: setRowSelection,
    enableRowSelection: canEditProject,
    getRowId: (row) => row.tag,
    state: {
      sorting,
      rowSelection,
    },
  });

  getVisibleRowIdsRef.current = (): string[] =>
    table.getRowModel().rows.map((row) => row.id);

  useEffect(() => {
    setRowSelection((prev) => {
      const valid = new Set(rows.map((r) => r.tag));
      const next = { ...prev };
      let changed = false;
      for (const k of Object.keys(next)) {
        if (!valid.has(k)) {
          delete next[k];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [rows, setRowSelection]);

  const tagsHeaderActions =
    canEditProject && shouldShowHeaderCreateActions(rows.length > 0, isMobile) ? (
      <AddItemButton
        onClick={openCreate}
        disabled={mergeSaving || bulkDeleting || createSaving || initialLoading}
        label={t('tag_manager_add_tag')}
      />
    ) : undefined;

  if (initialLoading) {
    return (
      <ProjectPanelLayout
        title={t('tag_manager_page_title')}
        titleIcon={PROJECT_SECTION_ICONS.tags}
        actions={tagsHeaderActions}
        loading
      />
    );
  }

  return (
    <>
      <ProjectPanelLayout
        title={t('tag_manager_page_title')}
        titleIcon={PROJECT_SECTION_ICONS.tags}
        actions={tagsHeaderActions}
      >
        <div className="flex flex-col min-w-0 overflow-hidden flex-1" style={{ minHeight: 0 }}>
          <div className="mb-4 flex w-full min-w-0 flex-col gap-3">
            <div className="flex w-full flex-wrap items-center gap-3">
              <div className="relative min-w-0 w-full min-[600px]:w-auto min-[600px]:flex-[2]">
                <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center">
                  <Search className="h-4 w-4 text-muted-foreground" aria-hidden />
                </span>
                <Input
                  type="text"
                  placeholder={t('tag_manager_search_placeholder')}
                  value={filterText}
                  onChange={(e) => setFilterText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      e.stopPropagation();
                    }
                  }}
                  className="min-w-[140px] w-full pl-9 pr-4 text-sm"
                  autoComplete="off"
                />
              </div>
              <TooltipProvider>
                <ButtonTooltip
                  tooltip={
                    showSimilarGroups
                      ? t('tag_manager_hide_similar_tags_tooltip')
                      : t('tag_manager_show_similar_tags_tooltip')
                  }
                  side="bottom"
                >
                  <Button
                    type="button"
                    variant="inverse"
                    size="sm"
                    onClick={handleToggleSimilarGroups}
                  >
                    {showSimilarGroups
                      ? t('tag_manager_hide_similar_tags')
                      : t('tag_manager_show_similar_tags')}
                  </Button>
                </ButtonTooltip>
              </TooltipProvider>
              {selectedGroupSet !== null && (
                <Button type="button" variant="ghost" size="sm" onClick={() => setSelectedGroupSet(null)}>
                  {t('tag_manager_clear_group_filter')}
                </Button>
              )}
              {canEditProject ? (
                <DisabledTooltip
                  enabled
                  text={
                    selectedTagNames.length < 2
                      ? t('tag_manager_merge_tags_disabled_tooltip')
                      : mergeSaving || bulkDeleting || createSaving
                        ? t('tag_manager_merge_tags_busy_tooltip')
                        : t('tag_manager_merge_tags_tooltip')
                  }
                >
                  <Button
                    type="button"
                    variant="inverse"
                    size="sm"
                    disabled={
                      selectedTagNames.length < 2 ||
                      mergeSaving ||
                      bulkDeleting ||
                      createSaving
                    }
                    onClick={() => {
                      setMergeName(mergeDefaultName);
                      setMergeOpen(true);
                    }}
                  >
                    {t('tag_manager_merge_tags')}
                  </Button>
                </DisabledTooltip>
              ) : null}
              {canEditProject ? (
                <DataTableSelectionActions
                  selectedCount={selectedTagNames.length}
                  countLabel={t('tag_manager_selected_count').replace(
                    '{count}',
                    String(selectedTagNames.length)
                  )}
                  onClear={clearSelection}
                  onDelete={requestBulkDelete}
                  deleting={bulkDeleting || mergeSaving || createSaving}
                />
              ) : null}
            </div>
            {showSimilarGroups && (
              <div className="flex flex-wrap gap-2 rounded-md border border-dashed border-border bg-muted/30 p-3">
                {similarGroups.length === 0 ? (
                  <p className="text-sm text-muted-foreground">{t('tag_manager_no_similar_groups')}</p>
                ) : (
                  similarGroups.map((group) => {
                    const sig = groupSignature(group);
                    const selected =
                      selectedGroupSet !== null &&
                      groupSignature(Array.from(selectedGroupSet)) === sig;
                    return (
                      <button
                        key={sig}
                        type="button"
                        title={group.join(', ')}
                        onClick={() => handleSimilarGroupClick(group)}
                        className={cn(
                          'inline-flex max-w-full min-w-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-left text-xs transition-colors',
                          selected
                            ? 'border-primary bg-primary/10 text-foreground'
                            : 'border-border bg-background hover:bg-muted'
                        )}
                      >
                        <span className="min-w-0 truncate">{formatSimilarGroupChipLabel(group)}</span>
                        <span className="shrink-0 tabular-nums text-muted-foreground">({group.length})</span>
                      </button>
                    );
                  })
                )}
              </div>
            )}
          </div>
          <div
            className="border border-border rounded-lg overflow-auto flex-1 flex flex-col"
            style={{ minHeight: 0 }}
          >
            <Table className="w-full">
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <TableHead key={header.id}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(header.column.columnDef.header, header.getContext())}
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {table.getRowModel().rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={columns.length} className="text-center py-12 text-muted-foreground">
                      {rows.length === 0
                        ? t('tag_manager_no_tags_in_project')
                        : t('tag_manager_no_matches')}
                    </TableCell>
                  </TableRow>
                ) : (
                  table.getRowModel().rows.map((row) => (
                    <TableRow
                      key={row.id}
                      className="cursor-pointer hover:bg-muted"
                      onClick={(event) => {
                        if (canEditProject) {
                          handleRowClick(row.id, event, () => openDetail(row.original));
                          return;
                        }
                        openDetail(row.original);
                      }}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </ProjectPanelLayout>

      <Dialog open={detailRow !== null} onOpenChange={(open) => !open && setDetailRow(null)}>
        <DialogContent className={`${ENTITY_DETAIL_DIALOG_CONTENT_CLASSNAME} sm:max-w-lg`}>
          <DialogTitle className="sr-only">{t('tag_manager_associated_entities')}</DialogTitle>
          <EntityDetailModalLayout
            header={
              <EntityDetailModalTitle>{t('tag_manager_associated_entities')}</EntityDetailModalTitle>
            }
            footer={
              <DialogFooter className="gap-2 sm:justify-end">
                <CancelButton type="button" onClick={() => setDetailRow(null)}>
                  {canEditProject ? t('cancel') : (t('close') || 'Close')}
                </CancelButton>
                {canEditProject && (
                  <Button
                    type="button"
                    onClick={() => void handleDetailSave()}
                    disabled={detailSaving}
                    dialogPrimary
                    className="inline-flex items-center gap-1.5"
                  >
                    {t('save')}
                    {!detailSaving && <DialogPrimaryShortcut className="ml-0.5" />}
                  </Button>
                )}
              </DialogFooter>
            }
          >
            <div className="flex flex-col gap-4 p-4">
              <div className="space-y-2">
                <Label htmlFor="tag-detail-name">{t('Name')}</Label>
                <Input
                  id="tag-detail-name"
                  value={detailName}
                  onChange={(e) => setDetailName(e.target.value)}
                  onKeyDown={(e) => e.stopPropagation()}
                  readOnly={!canEditProject}
                  disabled={!canEditProject}
                  autoFocus={canEditProject}
                />
              </div>
              <div className="text-sm font-medium text-muted-foreground">{t('tag_manager_linked_list')}</div>
              <ul className="list-none space-y-2">
                {detailRow && detailRow.entities.length === 0 ? (
                  <li className="text-sm text-muted-foreground">{t('tag_manager_no_entities')}</li>
                ) : (
                  detailRow?.entities.map((entity) => (
                    <li key={`${entity.type}-${entity.id}`}>
                      <button
                        type="button"
                        className="flex w-full items-start gap-2 text-left text-sm text-primary hover:underline"
                        aria-label={`${t(getEntityTypeLabelKey(entity.type))}: ${entity.title || entity.id}`}
                        onClick={(e) => {
                          handleModifierAwareNavigate(e, navigate, entityLink(entity), {
                            afterSameTabNavigate: () => setDetailRow(null),
                          });
                        }}
                        onAuxClick={(e) => {
                          handleModifierAwareNavigate(e, navigate, entityLink(entity));
                        }}
                      >
                        <EntityTypeIcon
                          type={entity.type}
                          typeLabel={t(getEntityTypeLabelKey(entity.type))}
                          className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground"
                        />
                        <span className="min-w-0 flex-1">{entity.title || entity.id}</span>
                      </button>
                    </li>
                  ))
                )}
              </ul>
            </div>
          </EntityDetailModalLayout>
        </DialogContent>
      </Dialog>

      <Dialog open={mergeOpen} onOpenChange={setMergeOpen}>
        <DialogContent className={`${ENTITY_DETAIL_DIALOG_CONTENT_CLASSNAME} sm:max-w-lg`}>
          <DialogTitle className="sr-only">{t('tag_manager_merge_dialog_title')}</DialogTitle>
          <EntityDetailModalLayout
            header={
              <EntityDetailModalTitle>{t('tag_manager_merge_dialog_title')}</EntityDetailModalTitle>
            }
            footer={
              <DialogFooter className="gap-2 sm:justify-end">
                <CancelButton type="button" onClick={() => setMergeOpen(false)}>
                  {t('cancel')}
                </CancelButton>
                <Button
                  type="button"
                  onClick={() => void handleMergeSave()}
                  disabled={mergeSaving || !mergeName.trim() || selectedTagNames.length < 2}
                  dialogPrimary
                  className="inline-flex items-center gap-1.5"
                >
                  {t('save')}
                  {!mergeSaving && <DialogPrimaryShortcut className="ml-0.5" />}
                </Button>
              </DialogFooter>
            }
          >
            <div className="flex flex-col gap-4 p-4">
              <p className="text-sm text-muted-foreground">{t('tag_manager_merge_description')}</p>
              <div className="space-y-2">
                <Label htmlFor="merge-tag-name">{t('tag_manager_rename_tag')}</Label>
                <Input
                  id="merge-tag-name"
                  value={mergeName}
                  onChange={(e) => setMergeName(e.target.value)}
                  onKeyDown={(e) => e.stopPropagation()}
                  autoFocus
                />
              </div>
            </div>
          </EntityDetailModalLayout>
        </DialogContent>
      </Dialog>

      <Dialog
        open={createOpen}
        onOpenChange={(open) => {
          if (!open && !createSaving) {
            setCreateOpen(false);
          }
        }}
      >
        <DialogContent className={`${ENTITY_DETAIL_DIALOG_CONTENT_CLASSNAME} sm:max-w-lg`}>
          <DialogTitle className="sr-only">{t('tag_manager_add_tag')}</DialogTitle>
          <EntityDetailModalLayout
            header={<EntityDetailModalTitle>{t('tag_manager_add_tag')}</EntityDetailModalTitle>}
            footer={
              <DialogFooter className="gap-2 sm:justify-end">
                <CancelButton
                  type="button"
                  onClick={() => setCreateOpen(false)}
                  disabled={createSaving}
                >
                  {t('cancel')}
                </CancelButton>
                <Button
                  type="button"
                  onClick={() => void handleCreateSave()}
                  disabled={createSaving || !createName.trim()}
                  dialogPrimary
                  className="inline-flex items-center gap-1.5"
                >
                  {createSaving ? t('adding') : t('add')}
                  {!createSaving && <DialogPrimaryShortcut className="ml-0.5" />}
                </Button>
              </DialogFooter>
            }
          >
            <div className="flex flex-col gap-4 p-4">
              <div className="space-y-2">
                <Label htmlFor="create-tag-name">{t('Name')}</Label>
                <Input
                  id="create-tag-name"
                  value={createName}
                  onChange={(e) => setCreateName(e.target.value)}
                  onKeyDown={(e) => e.stopPropagation()}
                  placeholder={t('tag_manager_create_placeholder')}
                  autoFocus
                />
              </div>
            </div>
          </EntityDetailModalLayout>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={pendingDeleteTags.length > 0}
        onOpenChange={(open) => {
          if (!open) {
            setPendingDeleteTags([]);
          }
        }}
        title={
          pendingDeleteTags.length === 1
            ? t('tag_manager_delete_tag')
            : t('tag_manager_delete_tags')
        }
        description={
          pendingDeleteTags.length === 1
            ? t('tag_manager_confirm_delete_tag')
            : t('tag_manager_confirm_delete_tags').replace(
                '{{count}}',
                String(pendingDeleteTags.length)
              )
        }
        confirmLabel={t('delete')}
        onConfirm={confirmPendingDelete}
      />
    </>
  );
};
